import { logger } from "@scott/logger";

/**
 * Counts the catalogue requests one listing makes and logs them in a single
 * line, so a change to the listing can be judged by how many calls it costs
 * and how long they take instead of by page load times alone.
 *
 * Switched on per request with `?plp-stats=true` on the page (see
 * `utils/plp-stats-param.ts`), so nothing is measured or logged otherwise.
 *
 * Only calls that reach SAP through `runCatalogueRequest` are counted. A call
 * answered by React's `cache` or `unstable_cache` never gets there; one answered
 * by Next's fetch cache does, and shows up as a short duration.
 */
export type CatalogueRequestKind =
  | "search"
  | "product"
  | "article"
  | "count"
  | "facets";

interface RequestStats {
  calls: Record<CatalogueRequestKind, number>;
  failed: number;
  slowestMs: number;
}

interface StatsStorage {
  getStore(): RequestStats | undefined;
  run<R>(stats: RequestStats, callback: () => R): R;
}

// Next puts `AsyncLocalStorage` on the global object of its server. It is read
// from there because importing `node:async_hooks` makes Turbopack fail when a
// client bundle reaches this module through the PDP adapter. Where the global
// is missing (browser) the stats stay off.
let statsStorage: StatsStorage | undefined;
function getStatsStorage(): StatsStorage | undefined {
  const AsyncLocalStorage = (
    globalThis as { AsyncLocalStorage?: new () => StatsStorage }
  ).AsyncLocalStorage;

  if (!statsStorage && typeof AsyncLocalStorage === "function") {
    statsStorage = new AsyncLocalStorage();
  }

  return statsStorage;
}

/** Whether a listing is being measured right now. */
export function isCatalogueRequestStatsActive(): boolean {
  return getStatsStorage()?.getStore() !== undefined;
}

/** Notes one finished request on the listing that is currently being loaded. */
export function recordCatalogueRequest(
  kind: CatalogueRequestKind,
  durationMs: number,
  hasFailed: boolean
): void {
  const stats = getStatsStorage()?.getStore();
  if (!stats) {
    return;
  }

  stats.calls[kind] += 1;
  stats.slowestMs = Math.max(stats.slowestMs, durationMs);
  if (hasFailed) {
    stats.failed += 1;
  }
}

/**
 * Runs `load` and, when `isRequested`, logs what its catalogue requests cost.
 * `label` names the loader and `context` says which listing it was.
 */
export async function withCatalogueRequestStats<T>(
  label: string,
  isRequested: boolean | undefined,
  context: Record<string, unknown>,
  load: () => Promise<T>
): Promise<T> {
  const storage = getStatsStorage();
  if (!(isRequested && storage)) {
    return await load();
  }

  const stats: RequestStats = {
    calls: { article: 0, count: 0, facets: 0, product: 0, search: 0 },
    failed: 0,
    slowestMs: 0,
  };
  const startedAt = performance.now();

  try {
    return await storage.run(stats, load);
  } finally {
    logger.info(`${label}: request stats`, {
      ...context,
      calls: stats.calls,
      failed: stats.failed,
      slowestMs: Math.round(stats.slowestMs),
      totalMs: Math.round(performance.now() - startedAt),
    });
  }
}
